import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AmbientAudioToggle from "@/components/AmbientAudioToggle";
import { useLocale } from "@/lib/locale";

export default function Header({ minimal = false }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const { t } = useLocale();
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!user || minimal) return;
        let cancelled = false;
        const fetchUnread = async () => {
            try {
                const { data } = await api.get("/inbox");
                if (!cancelled) setUnread(data.unread_count || 0);
            } catch {}
        };
        fetchUnread();
        const t = setInterval(fetchUnread, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user, minimal, loc.pathname]);

    return (
        <header className="sticky top-0 z-30 bg-ink-deep/90 backdrop-blur-md border-b border-ink-border/60 shadow-[0_1px_0_rgba(200,161,101,0.08)]">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 py-4 flex items-center justify-between gap-3 md:gap-6">
                <Link
                    to={user ? "/dashboard" : "/"}
                    data-testid="header-logo"
                    className="group flex items-center gap-3 md:gap-4 min-w-0 flex-shrink-0"
                    aria-label="Other Side — home"
                >
                    {/* Size-responsive: slightly smaller on mobile to leave
                        breathing room for the nav. Bumps back to 52 on sm+. */}
                    <BrandMark size={44} />
                    {/* Wordmark: only shown on xl+ (≥1280px). On large and
                        below the logo alone is identity enough and avoids
                        the gold glow overlapping the text. */}
                    <span className="hidden xl:flex items-baseline gap-2 whitespace-nowrap min-w-0 ml-2">
                        <span className="font-serif text-[1.5rem] italic text-ink-textHigh brand-wordmark tracking-wide leading-none whitespace-nowrap">
                            Other Side
                        </span>
                        <span className="label-eyebrow text-ink-textMid hidden 2xl:inline ml-2 whitespace-nowrap">{t("header.sub")}</span>
                    </span>
                </Link>
                {!minimal && (
                    <nav className="flex items-center gap-2 sm:gap-3 md:gap-5 text-sm whitespace-nowrap flex-shrink min-w-0">
                        {user ? (
                            <>
                                <Link to="/dashboard" className="btn-ghost hidden sm:inline" data-testid="nav-dashboard">{t("nav.presences")}</Link>
                                <Link to="/inbox" className="btn-ghost relative inline-flex items-center gap-1.5" data-testid="nav-inbox">
                                    {t("nav.inbox")}
                                    {unread > 0 && (
                                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-ink-gold text-ink-deep rounded-full px-1" data-testid="inbox-badge">
                                            {unread}
                                        </span>
                                    )}
                                </Link>
                                <Link to="/fallback-chat" className="btn-ghost hidden lg:inline" data-testid="nav-fallback">{t("nav.quietroom")}</Link>
                                <Link to="/pricing" className="btn-ghost hidden md:inline" data-testid="nav-upgrade">{t("nav.plan")}</Link>
                                <Link to="/settings" className="btn-ghost hidden md:inline" data-testid="nav-settings">{t("nav.settings")}</Link>
                                <button
                                    data-testid="nav-logout"
                                    onClick={async () => { await logout(); nav("/"); }}
                                    className="btn-ghost whitespace-nowrap hidden md:inline"
                                >{t("nav.signout")}</button>
                                <LanguageSwitcher compact />
                                <AmbientAudioToggle />
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="btn-ghost" data-testid="nav-login">{t("nav.signin")}</Link>
                                <Link to="/signup" className="btn-primary !py-2 !px-4 text-sm" data-testid="nav-signup">{t("nav.begin")}</Link>
                                <LanguageSwitcher compact />
                                <AmbientAudioToggle />
                            </>
                        )}
                    </nav>
                )}
                {minimal && (
                    <>
                        <LanguageSwitcher compact />
                        <AmbientAudioToggle />
                    </>
                )}
            </div>
        </header>
    );
}
